import React, { useEffect, useState } from "react";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgPaper,
  IgSwitch,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { ConfigurationDto } from "@intelligems/ig-types";
import { Grid, Skeleton } from "@mui/material";
import { useSyncOfferMetafields } from "@src/api/app-discount-functions/sync-app-discount-functions";
import { usePatchConfigurations } from "@src/api/configurations/patch-configurations";
import { useResetDemoStores } from "@src/api/meta/reset-demo-stores";
import { useGetOrganizationSummary } from "@src/api/organization-summaries/get-organization-summary";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useSyncScopes } from "@src/api/subscriptions/sync-scopes";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useIsIntelligems } from "@src/api/user-auths/is-intelligems";
import { RequestBulkOrders } from "@src/domains/Settings/AdminSettings/AdvancedSettings/RequestBulkOrders";
import { useDispatch, useSelector } from "@src/store";
import {
  selectedViewAsMode,
  setViewAsMode,
  setViewAsProductId,
} from "@src/store/slices/appSettings";
import { toProperCaseWithSpaces } from "@src/utils";

export const AdvancedActions = () => {
  const orgSummary = useGetOrganizationSummary();
  const syncScopes = useSyncScopes();
  const resetDemoStores = useResetDemoStores();
  const syncOfferMetafields = useSyncOfferMetafields();
  const dispatch = useDispatch();
  const viewAsMode = useSelector(selectedViewAsMode);

  const { data, isLoading: isOrgDataLoading } = useGetOrg();
  const configs = data.org.configurations;

  const shopifyFunctionsEnabled = configs.find(
    (c) => c.key === "shopify_functions_enabled"
  );
  const onboardingTestFlowConfig = configs.find(
    (c) => c.key === "onboarding_test_flow"
  );

  const [onboardingTestFlowFormState, setOnboardingTestFlowFormState] =
    useImmer<ConfigurationDto | undefined>(undefined);

  useEffect(() => {
    if (onboardingTestFlowConfig) {
      setOnboardingTestFlowFormState(() => onboardingTestFlowConfig);
    }
  }, [onboardingTestFlowConfig, setOnboardingTestFlowFormState]);

  const handleChange = (newValue: string) => {
    setOnboardingTestFlowFormState((draft) => {
      if (draft) draft.value = newValue;
    });
  };

  const { mutate: patchConfig, isPending: isPatchConfigPending } =
    usePatchConfigurations();

  const isAdmin = useIsAdmin();
  const isIntelligems = useIsIntelligems();

  const onSyncOfferMetafieldsClick = () => {
    syncOfferMetafields.mutate();
  };

  if (!isIntelligems) {
    return null;
  }

  return (
    <IgPaper>
      <Grid container rowSpacing={3} direction={"column"}>
        <Grid item xs={12}>
          <IgTypography variant="h3" fontWeight={"fontWeightMedium"}>
            Advanced Actions (Admin Only)
          </IgTypography>
        </Grid>
        <Grid item xs={6}>
          <Grid container alignItems="center" columnSpacing={3}>
            <Grid item sx={{ minWidth: "350px" }}>
              <IgTypography>Reset Demo Stores</IgTypography>
            </Grid>
            <Grid item>
              <IgButton
                sx={{ width: "200px" }}
                loading={resetDemoStores.isPending}
                variant="outlined"
                onClick={() => {
                  resetDemoStores.mutate();
                }}
              >
                Reset Demo Stores
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container alignItems="center" columnSpacing={3}>
            <Grid item sx={{ minWidth: "350px" }}>
              <IgTypography>Sync Scopes</IgTypography>
            </Grid>
            <Grid item>
              <IgButton
                sx={{ width: "200px" }}
                variant="outlined"
                size="large"
                onClick={() => {
                  if (orgSummary.isSuccess) {
                    syncScopes.mutate();
                  }
                }}
                loading={syncScopes.isPending}
              >
                Sync Scopes
              </IgButton>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="center">
            <RequestBulkOrders />
            <Grid item xs={6}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item sx={{ minWidth: "350px" }}>
                  <IgTypography>Enable View As Mode:</IgTypography>
                </Grid>
                <Grid item xs={1} textAlign={"end"}>
                  <IgSwitch
                    checked={viewAsMode || false}
                    onChange={(e) => {
                      dispatch(setViewAsMode(e.target.checked));
                      if (!e.target.checked) {
                        dispatch(setViewAsProductId(undefined));
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {isOrgDataLoading && (
          <Grid>
            <Skeleton />
          </Grid>
        )}

        {!isOrgDataLoading && shopifyFunctionsEnabled?.value && isAdmin && (
          <Grid item xs={6}>
            <Grid container alignItems="center" columnSpacing={3}>
              <Grid item sx={{ minWidth: "350px" }}>
                {" "}
                <IgTypography>Shopify Functions</IgTypography>
              </Grid>
              <Grid item>
                <IgButton
                  sx={{ width: "200px" }}
                  variant="outlined"
                  size="large"
                  onClick={onSyncOfferMetafieldsClick}
                  loading={syncOfferMetafields.isPending}
                >
                  Sync Functions
                </IgButton>
              </Grid>
            </Grid>
          </Grid>
        )}
        {isOrgDataLoading && (
          <Grid>
            <Skeleton />
          </Grid>
        )}
        {!isOrgDataLoading &&
          onboardingTestFlowConfig &&
          onboardingTestFlowFormState && (
            <Grid item>
              <Grid container spacing={2}>
                <Grid item xs={9}>
                  <IgTextField
                    onChange={(e) => handleChange(e.target.value)}
                    value={onboardingTestFlowFormState.value}
                    orientation="horizontal"
                    label={toProperCaseWithSpaces(onboardingTestFlowConfig.key)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <IgButton
                    sx={{ width: "120px" }}
                    variant="contained"
                    size="large"
                    disabled={
                      onboardingTestFlowFormState.value ===
                      onboardingTestFlowConfig.value
                    }
                    onClick={() => patchConfig([onboardingTestFlowFormState])}
                    loading={isPatchConfigPending}
                  >
                    Save
                  </IgButton>
                </Grid>
              </Grid>
            </Grid>
          )}
      </Grid>
    </IgPaper>
  );
};
